'use client';

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@highereducation/hen-ui';

import { useGlobalProps } from '@hen-site/component/GlobalContextProvider';
import { canonicalizeCmsUrl } from '@hen-site/utils/cmsUrl';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const TOOLBAR_COOKIE_NAME = 'wp_preview_logged_in';
export const HEN_ADMIN_TOOLBAR_HEIGHT_PROPERTY = '--hen-admin-toolbar-height';

const CONTROL_CLASS =
  'flex w-fit items-center justify-center gap-2 p-2 text-l1 text-white hover:bg-neutral-500';

type HenAdminToolbarProps = {
  cmsUrl: string;
  domainLabelWithExtension: string;
  postTypeGql?: string;
};

type CmsUrls = {
  admin: string;
  create: string;
  edit?: string;
};

const PUBLIC_SUFFIX_COOKIE_DOMAINS = new Set([
  'wpengine.com',
  'wpenginepowered.com',
]);

export function hasToolbarCookie(cookieHeader: string): boolean {
  return cookieHeader.split(';').some((cookie) => {
    const separatorIndex = cookie.indexOf('=');
    const name =
      separatorIndex === -1
        ? cookie.trim()
        : cookie.slice(0, separatorIndex).trim();
    return name === TOOLBAR_COOKIE_NAME;
  });
}

export function getWordPressCookieDomain(cmsUrl: string): string | undefined {
  try {
    const hostname = new URL(canonicalizeCmsUrl(cmsUrl)).hostname
      .toLowerCase()
      .replace(/\.$/, '');
    const isLocalHostname =
      hostname === 'localhost' ||
      hostname === '[::1]' ||
      /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);

    if (isLocalHostname || !hostname.includes('.')) {
      return undefined;
    }

    const labels = hostname.split('.');
    const cookieDomain =
      labels.length >= 3 ? labels.slice(1).join('.') : hostname;

    if (
      PUBLIC_SUFFIX_COOKIE_DOMAINS.has(hostname) ||
      PUBLIC_SUFFIX_COOKIE_DOMAINS.has(cookieDomain)
    ) {
      return undefined;
    }

    return cookieDomain;
  } catch {
    return undefined;
  }
}

function buildCmsUrls(
  cmsUrl: string,
  postTypeGql: string,
  pageId?: string,
): CmsUrls | undefined {
  try {
    const cmsOrigin = new URL(canonicalizeCmsUrl(cmsUrl));
    if (!['http:', 'https:'].includes(cmsOrigin.protocol)) {
      return undefined;
    }

    const adminUrl = new URL('/wp-admin/', cmsOrigin);
    const createUrl = new URL('post-new.php', adminUrl);
    createUrl.searchParams.set('post_type', postTypeGql.toLowerCase());

    const editUrl = pageId ? new URL('post.php', adminUrl) : undefined;
    if (editUrl && pageId) {
      editUrl.searchParams.set('post', pageId);
      editUrl.searchParams.set('action', 'edit');
    }

    return {
      admin: adminUrl.toString(),
      create: createUrl.toString(),
      edit: editUrl?.toString(),
    };
  } catch {
    return undefined;
  }
}

export function buildToolbarCookieExpirations(cmsUrl: string): string[] {
  const attributes = 'Max-Age=0; Path=/; SameSite=Lax';
  const expirations = [`${TOOLBAR_COOKIE_NAME}=; ${attributes}`];
  const cookieDomain = getWordPressCookieDomain(cmsUrl);

  if (cookieDomain) {
    expirations.push(
      `${TOOLBAR_COOKIE_NAME}=; ${attributes}; Domain=${cookieDomain}`,
    );
  }

  return expirations;
}

function clearToolbarCookie(cmsUrl: string): void {
  for (const expiration of buildToolbarCookieExpirations(cmsUrl)) {
    document.cookie = expiration;
  }
}

function normalizePageId(pageId: unknown): string | undefined {
  const value = typeof pageId === 'string' ? pageId.trim() : '';
  return /^\d+$/.test(value) && Number(value) > 0 ? value : undefined;
}

export function HenAdminToolbar({
  cmsUrl,
  domainLabelWithExtension,
  postTypeGql = 'page',
}: HenAdminToolbarProps) {
  const { id } = useGlobalProps();
  const [isVisible, setIsVisible] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  const toolbarRef = useRef<HTMLElement>(null);
  const copyStatusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const pageId = normalizePageId(id);
  const adminLabel = domainLabelWithExtension.trim()
    ? domainLabelWithExtension
    : 'WordPress Admin';
  const urls = useMemo(
    () => buildCmsUrls(cmsUrl, postTypeGql, pageId),
    [cmsUrl, pageId, postTypeGql],
  );

  useEffect(() => {
    setIsVisible(hasToolbarCookie(document.cookie));
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!isVisible || !toolbarRef.current) {
      document.documentElement.style.removeProperty(
        HEN_ADMIN_TOOLBAR_HEIGHT_PROPERTY,
      );
      return;
    }

    const toolbar = toolbarRef.current;
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        HEN_ADMIN_TOOLBAR_HEIGHT_PROPERTY,
        `${toolbar.getBoundingClientRect().height}px`,
      );
    };

    updateHeight();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(updateHeight);
    observer?.observe(toolbar);

    return () => {
      observer?.disconnect();
      document.documentElement.style.removeProperty(
        HEN_ADMIN_TOOLBAR_HEIGHT_PROPERTY,
      );
    };
  }, [isVisible]);

  useEffect(
    () => () => {
      if (copyStatusTimerRef.current) {
        clearTimeout(copyStatusTimerRef.current);
      }
    },
    [],
  );

  if (!isVisible || !urls) {
    return null;
  }

  const resetCopyStatusLater = () => {
    if (copyStatusTimerRef.current) {
      clearTimeout(copyStatusTimerRef.current);
    }
    copyStatusTimerRef.current = setTimeout(() => setCopyStatus('idle'), 2000);
  };

  const copyPageId = async () => {
    if (!pageId || !navigator.clipboard) {
      setCopyStatus('failed');
      resetCopyStatusLater();
      return;
    }

    try {
      await navigator.clipboard.writeText(pageId);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    resetCopyStatusLater();
  };

  const closeToolbar = () => {
    clearToolbarCookie(cmsUrl);
    document.documentElement.style.removeProperty(
      HEN_ADMIN_TOOLBAR_HEIGHT_PROPERTY,
    );
    setIsVisible(false);
  };

  return (
    <aside
      ref={toolbarRef}
      data-testid='hen-admin-toolbar'
      aria-label='WordPress editing tools'
      className='not-prose sticky top-0 z-[60] flex justify-between bg-neutral-700 text-b5 text-white'
    >
      <div className='flex flex-wrap gap-2'>
        <a
          className={CONTROL_CLASS}
          href={urls.admin}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Icon icon='external-link' size='sm' aria-hidden={true} />
          {adminLabel}
        </a>
        <a
          className={CONTROL_CLASS}
          href={urls.create}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Icon icon='plus' size='sm' aria-hidden={true} />
          Create Page
        </a>
        {urls.edit && (
          <a
            className={CONTROL_CLASS}
            href={urls.edit}
            target='_blank'
            rel='noopener noreferrer'
          >
            <Icon icon='pencil' size='sm' aria-hidden={true} />
            Edit
          </a>
        )}
        {pageId && (
          <button type='button' className={CONTROL_CLASS} onClick={copyPageId}>
            Post ID:
            <span aria-live='polite'>
              {copyStatus === 'copied' ? (
                'Copied!'
              ) : copyStatus === 'failed' ? (
                'Copy failed'
              ) : (
                <span className='underline'>{pageId}</span>
              )}
            </span>
          </button>
        )}
      </div>
      <button
        type='button'
        className='bg-neutral-900 px-4 py-2 text-l1 text-white'
        onClick={closeToolbar}
      >
        Close Toolbar
      </button>
    </aside>
  );
}
